/**
 * TokensTab.js — Counselor token registration & batch print
 * CR-007 G-2: Self-service token generation with QR card printing
 */
import React, { useState } from "react";
import S from "./adminStyles";

const BATCH_OPTIONS = [1, 4, 8, 12, 16, 20];
const SCHOOL_ID = "riverwood";

export default function TokensTab({ user }) {
  const [mode, setMode] = useState("single");
  const [householdSize, setHouseholdSize] = useState("4");
  const [batchCount, setBatchCount] = useState("4");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  const [generatedTokens, setGeneratedTokens] = useState([]);

  const handleSingleRegister = async () => {
    setMessage(null);
    setSubmitting(true);
    try {
      const authToken = await user.getIdToken();
      const res = await fetch("/api/households", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          schoolId: SCHOOL_ID,
          householdSize: parseInt(householdSize),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setGeneratedTokens([data]);
        setMessage({ type: "success", text: `Token ${data.tokenId} registered successfully` });
      } else {
        setMessage({ type: "error", text: data.error || "Registration failed" });
      }
    } catch (err) {
      console.error("Registration error:", err);
      setMessage({ type: "error", text: "Network error — please try again" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBatchRegister = async () => {
    setMessage(null);
    const count = parseInt(batchCount);
    if (!window.confirm(`You are about to register ${count} new tokens. This cannot be undone. Continue?`)) {
      return;
    }
    setSubmitting(true);
    try {
      const authToken = await user.getIdToken();
      const res = await fetch("/api/households/batch", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          schoolId: SCHOOL_ID,
          count,
          householdSize: parseInt(householdSize),
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setGeneratedTokens(data.tokens);
        setMessage({ type: "success", text: `${data.count} tokens registered successfully (${data.tokens[0].tokenId} – ${data.tokens[data.count - 1].tokenId})` });
      } else {
        setMessage({ type: "error", text: data.error || "Batch registration failed" });
      }
    } catch (err) {
      console.error("Batch registration error:", err);
      setMessage({ type: "error", text: "Network error — please try again" });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div>
      {/* Registration controls — hidden when printing */}
      <div className="no-print">
        <h3 style={{ marginBottom: 16 }}>🎫 Register New Tokens</h3>

        {message && (
          <div style={{
            padding: "10px 14px",
            marginBottom: 16,
            borderRadius: 6,
            backgroundColor: message.type === "error" ? "#fce4ec" : "#e8f5e9",
            color: message.type === "error" ? "#c62828" : "#2e7d32",
            border: `1px solid ${message.type === "error" ? "#ef9a9a" : "#a5d6a7"}`,
          }}>
            {message.text}
          </div>
        )}

        {/* Mode toggle */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <button
            onClick={() => { setMode("single"); setGeneratedTokens([]); setMessage(null); }}
            style={{ ...S.btn, padding: "8px 16px", backgroundColor: mode === "single" ? "#1a3a1a" : "#e0e0e0", color: mode === "single" ? "#fff" : "#333" }}
          >
            Single Token
          </button>
          <button
            onClick={() => { setMode("batch"); setGeneratedTokens([]); setMessage(null); }}
            style={{ ...S.btn, padding: "8px 16px", backgroundColor: mode === "batch" ? "#1a3a1a" : "#e0e0e0", color: mode === "batch" ? "#fff" : "#333" }}
          >
            Batch Registration
          </button>
        </div>

        {/* Form fields */}
        <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ fontSize: 14 }}>
            Household Size:
            <select
              value={householdSize}
              onChange={(e) => setHouseholdSize(e.target.value)}
              style={{ ...S.input, marginLeft: 8, width: 70 }}
            >
              {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>

          {mode === "batch" && (
            <label style={{ fontSize: 14 }}>
              Number of Tokens:
              <select
                value={batchCount}
                onChange={(e) => setBatchCount(e.target.value)}
                style={{ ...S.input, marginLeft: 8, width: 70 }}
              >
                {BATCH_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
          )}

          <button
            onClick={mode === "single" ? handleSingleRegister : handleBatchRegister}
            disabled={submitting}
            style={{ ...S.btn, backgroundColor: "#1a3a1a", color: "#fff", padding: "8px 20px", opacity: submitting ? 0.6 : 1 }}
          >
            {submitting ? "Registering..." : mode === "single" ? "Register Token" : `Register ${batchCount} Tokens`}
          </button>
        </div>

        {/* Print button */}
        {generatedTokens.length > 0 && (
          <button
            onClick={handlePrint}
            style={{ ...S.btn, backgroundColor: "#1565c0", color: "#fff", padding: "10px 24px", fontSize: 15, marginBottom: 16 }}
          >
            🖨️ Print {generatedTokens.length === 1 ? "Card" : `All ${generatedTokens.length} Cards`}
          </button>
        )}
      </div>

      {/* Printable Token Cards */}
      {generatedTokens.length > 0 && (
        <div id="token-cards-print-area">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
            {generatedTokens.map((token, idx) => (
              <div
                key={token.tokenId}
                style={{
                  width: "100%",
                  height: "5.5in",
                  border: "1px dashed #999",
                  padding: "0.4in 0.3in",
                  boxSizing: "border-box",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "space-between",
                  pageBreakInside: "avoid",
                  breakInside: "avoid",
                }}
              >
                {/* Header */}
                <div style={{
                  width: "100%",
                  backgroundColor: "#1a3a1a",
                  color: "#fff",
                  textAlign: "center",
                  padding: "10px 8px",
                  borderRadius: 6,
                }}>
                  <div style={{ fontSize: 22, fontWeight: "bold" }}>Raider Depot</div>
                  <div style={{ fontSize: 14, marginTop: 2 }}>Riverwood International Charter School</div>
                </div>

                {/* QR Code */}
                <div style={{ textAlign: "center", margin: "12px 0" }}>
                  <img
                    src={token.qrCode}
                    alt={`QR code for ${token.tokenId}`}
                    style={{ width: "2.2in", height: "2.2in" }}
                  />
                </div>

                {/* Token Code — large and bold */}
                <div style={{
                  fontSize: 36,
                  fontWeight: "bold",
                  fontFamily: "monospace",
                  textAlign: "center",
                  letterSpacing: 2,
                  margin: "4px 0",
                }}>
                  {token.tokenId}
                </div>

                {/* Fallback instructions */}
                <div style={{
                  textAlign: "center",
                  fontSize: 14,
                  color: "#444",
                  lineHeight: 1.5,
                  margin: "8px 0",
                  padding: "8px 12px",
                  backgroundColor: "#f5f5f5",
                  borderRadius: 4,
                  width: "100%",
                }}>
                  <div><strong>Can't scan the QR code?</strong></div>
                  <div>Go to: <strong>riverwoodraiderdepot.org/order</strong></div>
                  <div>Enter your code: <strong>{token.tokenId}</strong></div>
                </div>

                {/* Help contact */}
                <div style={{
                  fontSize: 13,
                  color: "#666",
                  textAlign: "center",
                }}>
                  Need help? See your school counselor
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Print styles */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #token-cards-print-area, #token-cards-print-area * { visibility: visible; }
          #token-cards-print-area {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
          }
          #token-cards-print-area > div {
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-template-rows: 1fr 1fr;
            gap: 0;
            width: 100%;
            height: 100vh;
            justify-items: center;
            align-items: center;
          }
          .no-print { display: none !important; }
          @page { margin: 0.25in; size: letter; }
        }
      `}</style>
    </div>
  );
}
